import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {GigsFilters} from '@/api/gigs/types';
import {AvailableGig} from '@/types/gigs';

const MAX_FOLLOWERS = 100_000;
const MAX_COMPENSATION = 5_000;
const MIN_RANGE_VALUE = 0;
const MAX_ENGAGEMENT_BANNER_POSITION = 2;

// Filter Types
interface SectionDeliverable {
  photos?: boolean;
  videos?: boolean;
}

interface RangeSliderValues {
  curMin?: number;
  curMax?: number;
  min?: number;
  max?: number;
}

interface SectionCompensationType {
  fixed?: boolean;
  negotiable?: boolean;
}

export interface FilterState {
  deliverables: SectionDeliverable;
  socialNetworks: string[];
  socialFollowersRange: RangeSliderValues;
  compensationType: SectionCompensationType;
  compensationSum: RangeSliderValues;
  tags: string[];
  isDirty?: boolean;
}

type FilterKey = keyof FilterState;

// Define the state interface
interface GigsState {
  // Data
  gigs: AvailableGig[];

  // Filter state
  filters: FilterState;
  draftFilters: FilterState;
  currentQueryParams: GigsFilters;
  filterResultsCount: number | null;
  draftFilterResultsCount: number | null;

  // UI state
  showFiltersModal: boolean;

  // Engagement banner
  engagementBannerRandomPosition: number;
}

// Create default filter state
const createDefaultFilterState = (): FilterState => {
  return {
    deliverables: {photos: false, videos: false},
    socialNetworks: [],
    socialFollowersRange: {
      min: MIN_RANGE_VALUE,
      max: MAX_FOLLOWERS,
      curMin: MIN_RANGE_VALUE,
      curMax: MAX_FOLLOWERS,
    },
    compensationType: {fixed: false, negotiable: false},
    compensationSum: {
      min: MIN_RANGE_VALUE,
      max: MAX_COMPENSATION,
      curMin: MIN_RANGE_VALUE,
      curMax: MAX_COMPENSATION,
    },
    tags: [],
    isDirty: false,
  };
};

// Create the store using immer middleware but without actions inside
const gigsStore = create(
  immer<GigsState>(() => ({
    // Initial data
    gigs: [],
    filters: createDefaultFilterState(),
    draftFilters: createDefaultFilterState(),
    currentQueryParams: {},
    filterResultsCount: null,
    draftFilterResultsCount: null,
    showFiltersModal: false,
    engagementBannerRandomPosition: 0,
  }))
);

// Define actions outside of the store
const setGigs = (gigs: AvailableGig[]) => {
  gigsStore.setState((state) => {
    state.gigs = gigs;
  });
};

const setFilterResultsCount = (filterResultsCount: number | null) => {
  gigsStore.setState((state) => {
    state.filterResultsCount = filterResultsCount;
  });
};

const setDraftFilterResultsCount = (draftFilterResultsCount: number | null) => {
  gigsStore.setState((state) => {
    state.draftFilterResultsCount = draftFilterResultsCount;
  });
};

const updateFilter = (key: FilterKey, value: any) => {
  gigsStore.setState((state) => {
    if (key in state.draftFilters) {
      // Use type assertion carefully or ensure FilterState properties match keys
      (state.draftFilters as any)[key] = value;
      state.draftFilters.isDirty = true;
    }
  });
};

// Helper function to deep compare filter states
const areFiltersEqual = (
  filters1: FilterState,
  filters2: FilterState
): boolean => {
  // Compare deliverables
  if (
    filters1.deliverables.photos !== filters2.deliverables.photos ||
    filters1.deliverables.videos !== filters2.deliverables.videos
  ) {
    return false;
  }

  // Compare social networks arrays
  if (
    filters1.socialNetworks.length !== filters2.socialNetworks.length ||
    !filters1.socialNetworks.every(
      (network, index) => network === filters2.socialNetworks[index]
    )
  ) {
    return false;
  }

  // Compare social followers range
  const range1 = filters1.socialFollowersRange;
  const range2 = filters2.socialFollowersRange;
  if (range1.curMin !== range2.curMin || range1.curMax !== range2.curMax) {
    return false;
  }

  // Compare compensation type
  if (
    filters1.compensationType.fixed !== filters2.compensationType.fixed ||
    filters1.compensationType.negotiable !==
      filters2.compensationType.negotiable
  ) {
    return false;
  }

  // Compare compensation sum range
  const comp1 = filters1.compensationSum;
  const comp2 = filters2.compensationSum;
  if (comp1.curMin !== comp2.curMin || comp1.curMax !== comp2.curMax) {
    return false;
  }

  // Compare tags arrays
  if (
    filters1.tags.length !== filters2.tags.length ||
    !filters1.tags.every((tag, index) => tag === filters2.tags[index])
  ) {
    return false;
  }

  return true;
};

const applyFilters = (
  setSearchParams: (
    params: URLSearchParams,
    options?: {replace?: boolean}
  ) => void
): boolean => {
  // Get current state and check if filters have actually changed
  const currentState = gigsStore.getState();

  if (areFiltersEqual(currentState.filters, currentState.draftFilters)) {
    // Filters haven't changed, just close the modal
    gigsStore.setState((state) => {
      state.showFiltersModal = false;
    });
    return false;
  }

  // Filters have changed, proceed with full update
  const newFilters = {...currentState.draftFilters};
  const newQueryParams = buildQueryParams(newFilters);
  const newSearchParams = Object.entries(newQueryParams).reduce(
    (acc, [key, value]) => {
      if (value) acc.set(key, value.toString());
      return acc;
    },
    new URLSearchParams()
  );
  setSearchParams(newSearchParams, {replace: true});

  gigsStore.setState((state) => {
    state.filters = newFilters;
    state.currentQueryParams = newQueryParams;
    state.showFiltersModal = false;
  });

  return true;
};

const resetFilters = (
  setSearchParams: (
    params: URLSearchParams,
    options?: {replace?: boolean}
  ) => void
): boolean => {
  // Get current state to check if there are any filters applied
  const currentState = gigsStore.getState();

  if (!currentState.filters.isDirty) {
    // No filters applied, just close the modal
    gigsStore.setState((state) => {
      state.showFiltersModal = false;
    });
    return false;
  }

  // Filters have been applied, proceed with reset
  const defaultFilters = createDefaultFilterState();
  setSearchParams(new URLSearchParams(), {replace: true});

  gigsStore.setState((state) => {
    state.filters = defaultFilters;
    state.draftFilters = defaultFilters;
    state.currentQueryParams = {};
    state.showFiltersModal = false;
  });

  return true;
};

const openFiltersModal = () => {
  gigsStore.setState((state) => {
    state.draftFilters = {...state.filters};
    state.showFiltersModal = true;
  });
};

const closeFiltersModal = () => {
  gigsStore.setState((state) => {
    state.draftFilters = {...state.filters};
    state.showFiltersModal = false;
  });
};

const initializeFromUrl = (searchParams: URLSearchParams) => {
  const initialFilters = parseSearchParamsToFilters(searchParams);
  const initialQueryParams = buildQueryParams(initialFilters);

  gigsStore.setState((state) => {
    state.filters = initialFilters;
    state.draftFilters = initialFilters;
    state.currentQueryParams = initialQueryParams;
  });
};

const randomizeEngagementBannerPosition = () => {
  const randomizedPosition = Math.floor(
    Math.random() * (MAX_ENGAGEMENT_BANNER_POSITION + 1)
  );

  gigsStore.setState((state) => {
    state.engagementBannerRandomPosition = randomizedPosition;
  });
};

// Build query params from filter state
export const buildQueryParams = (filters: FilterState): GigsFilters => {
  const params: GigsFilters = {};

  // Process deliverables
  const deliverableTypes: string[] = [];
  if (filters.deliverables?.photos) deliverableTypes.push('photos');
  if (filters.deliverables?.videos) deliverableTypes.push('videos');
  if (deliverableTypes.length > 0) {
    params.deliverable_type = deliverableTypes.join(',');
  }

  // Process social networks
  if (filters.socialNetworks?.length > 0) {
    params.network = filters.socialNetworks.join(',');
  }

  // Process followers range
  if (
    filters.socialFollowersRange?.curMin !== undefined &&
    filters.socialFollowersRange.curMin > 0
  ) {
    params.followers_min = filters.socialFollowersRange.curMin.toString();
  }

  if (
    filters.socialFollowersRange?.curMax !== undefined &&
    filters.socialFollowersRange.curMax < MAX_FOLLOWERS
  ) {
    params.followers_max = filters.socialFollowersRange.curMax.toString();
  }

  // Process compensation type
  const compensationTypes: string[] = [];
  if (filters.compensationType?.fixed) compensationTypes.push('fixed');
  if (filters.compensationType?.negotiable)
    compensationTypes.push('negotiable');
  if (compensationTypes.length > 0) {
    params.compensation_type = compensationTypes.join(',');
  }

  // Process compensation sum
  if (
    filters.compensationSum?.curMin !== undefined &&
    filters.compensationSum.curMin > 0
  ) {
    params.price_min = filters.compensationSum.curMin.toString();
  }

  if (
    filters.compensationSum?.curMax !== undefined &&
    filters.compensationSum.curMax < MAX_COMPENSATION
  ) {
    params.price_max = filters.compensationSum.curMax.toString();
  }

  // Process tags
  if (filters.tags?.length > 0) {
    params.tags = filters.tags.join(',');
  }

  return params;
};

// Parse URL search params to filter state
export const parseSearchParamsToFilters = (
  searchParams: URLSearchParams
): FilterState => {
  const defaultFilters = createDefaultFilterState();
  const result: FilterState = {...defaultFilters};

  // Parse deliverables
  const deliverableTypes =
    searchParams.get('deliverable_type')?.split(',') || [];
  if (deliverableTypes.length > 0) {
    result.deliverables = {
      photos: deliverableTypes.includes('photos'),
      videos: deliverableTypes.includes('videos'),
    };
  }

  // Parse social networks
  const networks = searchParams.get('network')?.split(',') || [];
  if (networks.length > 0) {
    result.socialNetworks = networks;
  }

  // Parse followers range
  const followersMin = searchParams.get('followers_min');
  const followersMax = searchParams.get('followers_max');
  if (followersMin || followersMax) {
    result.socialFollowersRange = {
      ...defaultFilters.socialFollowersRange,
      curMin: followersMin
        ? parseInt(followersMin, 10)
        : defaultFilters.socialFollowersRange.curMin,
      curMax: followersMax
        ? parseInt(followersMax, 10)
        : defaultFilters.socialFollowersRange.curMax,
    };
  }

  // Parse compensation type
  const compensationTypes =
    searchParams.get('compensation_type')?.split(',') || [];
  if (compensationTypes.length > 0) {
    result.compensationType = {
      fixed: compensationTypes.includes('fixed'),
      negotiable: compensationTypes.includes('negotiable'),
    };
  }

  // Parse compensation sum
  const priceMin = searchParams.get('price_min');
  const priceMax = searchParams.get('price_max');
  if (priceMin || priceMax) {
    result.compensationSum = {
      ...defaultFilters.compensationSum,
      curMin: priceMin
        ? parseInt(priceMin, 10)
        : defaultFilters.compensationSum.curMin,
      curMax: priceMax
        ? parseInt(priceMax, 10)
        : defaultFilters.compensationSum.curMax,
    };
  }

  // Parse tags
  const tags = searchParams.get('tags')?.split(',') || [];
  if (tags.length > 0) {
    result.tags = tags;
  }

  // Set isDirty if any filters are applied
  const hasAnyFilters = Object.keys(buildQueryParams(result)).length > 0;
  result.isDirty = hasAnyFilters;

  return result;
};

// Export actions
export const gigsActions = {
  setGigs,
  setFilterResultsCount,
  setDraftFilterResultsCount,
  updateFilter,
  applyFilters,
  resetFilters,
  openFiltersModal,
  closeFiltersModal,
  initializeFromUrl,
  randomizeEngagementBannerPosition,
};

// Create selector hooks
const useGigs = () => gigsStore((state) => state.gigs);
const useFilters = () => gigsStore((state) => state.filters);
const useDraftFilters = () => gigsStore((state) => state.draftFilters);
const useCurrentQueryParams = () =>
  gigsStore((state) => state.currentQueryParams);
const useFilterResultsCount = () =>
  gigsStore((state) => state.filterResultsCount);
const useDraftFilterResultsCount = () =>
  gigsStore((state) => state.draftFilterResultsCount);
const useShowFiltersModal = () => gigsStore((state) => state.showFiltersModal);
const useEngagementBannerRandomPosition = () =>
  gigsStore((state) => state.engagementBannerRandomPosition);

// Export the store with a "use" prefix for hook compatibility
export const useGigsStore = gigsStore;

// --- Exported Object Containing Hooks ---
const GigsStore = {
  // --- Selector Hooks ---
  useGigs,
  useFilters,
  useDraftFilters,
  useCurrentQueryParams,
  useFilterResultsCount,
  useDraftFilterResultsCount,
  useShowFiltersModal,
  useEngagementBannerRandomPosition,

  // --- Actions ---
  gigsActions,

  // Utility functions
  buildQueryParams,
  parseSearchParamsToFilters,
};

export default GigsStore;
